import { ChangeEvent, FunctionComponent, useEffect, useState } from "react"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import { Box } from "@mui/material"
import { FloatingInput, Button, Checkbox, AlertMUI } from "@icanbwell/ui-toolkit"
import { get } from "lodash"
import { useAnalyticsTrackEvent } from "@client-hub/shared/react/components/AnalyticsProvider"
import { ACTION, APP_NAME } from "@client-hub/shared/constants/analytics"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { selectRememberMeEmail } from "@client-hub/store/slices/metadata/selectors"
import { updateRememberMeEmail } from "@client-hub/store/slices/metadata"
import { routeToEventRoute } from "@client-hub/shared/utils/event"
import ROUTES from "@client-hub/shared/constants/routes"
import { useForm } from "react-hook-form"

const CredentialsSchema = Yup.object({
  username: Yup.string().email("Invalid email.").required("Email is required."),
  password: Yup.string().required("Password is required."),
}).required()
export type CredentialsData = Yup.InferType<typeof CredentialsSchema>

export type Props = {
  serverError: unknown
  onSubmit: (credentials: CredentialsData) => void
  onChange?: (credentials: CredentialsData) => void
  rememberMe: boolean
  sso?: boolean
}

const CredentialsForm: FunctionComponent<Props> = ({ serverError, onSubmit, onChange, rememberMe, sso }) => {
  const { t } = useTranslation()
  const rememberMeUserEmail = useSelector(selectRememberMeEmail)
  const [isRememberMeChecked, setIsRememberMeChecked] = useState(!!rememberMeUserEmail)
  const dispatch = useDispatch()
  const { trackEvent } = useAnalyticsTrackEvent()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
  } = useForm<CredentialsData>({
    resolver: yupResolver(CredentialsSchema),
  })
  const handleFormChange = () => onChange && onChange(getValues())
  useEffect(() => {
    if (rememberMeUserEmail) {
      setValue("username", rememberMeUserEmail)
    }
  }, [])
  /**
   * @description This method will set or remove the usermail from redux store on the basis of check of remember me.
   */
  const saveUsername = (e?: ChangeEvent<HTMLInputElement>) => {
    if (e) {
      setIsRememberMeChecked(e?.target.checked as boolean)
      if (e.target.checked) {
        dispatch(updateRememberMeEmail(getValues("username")))
      } else {
        dispatch(updateRememberMeEmail(undefined))
      }
    } else {
      if (rememberMeUserEmail) {
        dispatch(updateRememberMeEmail(getValues("username")))
      }
    }
  }

  return (
    <Box
      sx={{ "& > div": { mb: 2 }, width: "20em" }}
      component="form"
      autoComplete="off"
      display="flex"
      flexDirection="column"
      onSubmit={handleSubmit(onSubmit)}
      onChange={handleFormChange}
    >
      {
        // TODO: Use Notification System for this error once created.
        serverError && (
          <AlertMUI severity="error">{get(serverError, "data.detail", t("unknownError", { ns: "shared" }))}</AlertMUI>
        )
      }
      <FloatingInput
        id="credentials-email"
        label={t("forms.credentials.username")}
        {...register("username", {
          onBlur: () => saveUsername(),
        })}
        error={!!errors.username?.message}
        helperText={errors.username?.message}
      />
      {rememberMe && (
        <Checkbox
          sx={{
            ml: "auto",
            mb: 1,
            "& + .MuiTypography-root": {
              color: "white",
            },
          }}
          onChange={saveUsername}
          checked={isRememberMeChecked}
          label={t("forms.credentials.RememberMe")}
        />
      )}
      {!sso && (
        <>
          <FloatingInput
            id="credentials-password"
            sx={{ mt: 2 }}
            type="password"
            label={t("forms.credentials.password")}
            {...register("password")}
            error={!!errors.password?.message}
            helperText={errors.password?.message}
          />
          <Button
            sx={{ mt: 3 }}
            width="100%"
            id="credentials-submit-button"
            type="submit"
            text={isSubmitting ? t("forms.credentials.loggingIn") : t("forms.credentials.login")}
            disabled={isSubmitting}
            onClick={() =>
              trackEvent({
                appName: APP_NAME,
                route: routeToEventRoute(ROUTES.LOGIN.BASE),
                action: ACTION.CLICKED_SIGNIN,
                properties: getValues("username") ? { username: getValues("username") } : {},
              })
            }
          />
        </>
      )}
    </Box>
  )
}

export default CredentialsForm
